import { Button, Card, ErrorSummary } from '@beautyburst/ui';
import { input, semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { AuthOutcome, CustomerApiClient, SignUpInput } from '../api-client.ts';
import {
  resendCustomerVerificationCode,
  verifyCustomerEmail,
} from '../auth-verification-client.ts';
import {
  errorsForSummary,
  FormField,
  useFormErrors,
  validateEmail,
  validatePassword,
  validatePhone,
  validatePostalCode,
} from '../forms.tsx';

const SIGN_UP_FAILURE =
  'We could not create your account. Please review your details and try again.';
const VERIFICATION_FAILURE =
  'That verification code is not valid or has expired. Request a new code.';

function createSignupVisitorId(): string {
  return `visitor-${globalThis.crypto.randomUUID()}`;
}

export interface SignUpScreenProps {
  client: { readonly auth: Pick<CustomerApiClient['auth'], 'signUp'> };
  signupSource?: string;
  onSignedUp?: (outcome: Extract<AuthOutcome, { ok: true }>) => void;
}

export function SignUpScreen({ client, signupSource, onSignedUp }: SignUpScreenProps) {
  const { errors, setErrors, clear } = useFormErrors();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [marketingEmailConsent, setMarketingEmailConsent] = useState(false);
  const [marketingSmsConsent, setMarketingSmsConsent] = useState(false);
  const [visitorId] = useState(createSignupVisitorId);
  const [pending, setPending] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [created, setCreated] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (displayName.trim().length === 0)
      nextErrors['sign-up-display-name'] = 'Enter your display name.';
    const emailError = validateEmail(email);
    if (emailError !== undefined) nextErrors['sign-up-email'] = emailError;
    const passwordError = validatePassword(password);
    if (passwordError !== undefined) nextErrors['sign-up-password'] = passwordError;
    const phoneError = validatePhone(phone);
    if (phoneError !== undefined) nextErrors['sign-up-phone'] = phoneError;
    const postalCodeError = validatePostalCode(postalCode);
    if (postalCodeError !== undefined) nextErrors['sign-up-postal-code'] = postalCodeError;
    if (marketingSmsConsent && phone.trim().length === 0) {
      nextErrors['sign-up-phone'] = 'Enter a phone number to receive marketing text messages.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload: SignUpInput = {
      displayName: displayName.trim(),
      email: email.trim(),
      password,
      marketingEmailConsent,
      marketingSmsConsent: phone.trim().length > 0 && marketingSmsConsent,
      ...(phone.trim().length === 0 ? {} : { phone: phone.trim() }),
      ...(postalCode.trim().length === 0 ? {} : { postalCode: postalCode.trim() }),
      ...(signupSource === undefined ? {} : { signupSource }),
      ...(signupSource === undefined ? {} : { visitorId }),
    };

    setPending(true);
    try {
      await client.auth.signUp(payload);
      clear();
      // Registration deliberately returns the same pending-verification shape for an
      // address that already exists so the browser cannot reveal account existence.
      // A fulfilled request therefore always advances to verification; transport,
      // validation, and other real API failures still reject and land in catch below.
      setAwaitingVerification(true);
    } catch {
      setErrors({ 'sign-up-email': SIGN_UP_FAILURE });
    } finally {
      setPending(false);
    }
  };

  const verify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedCode = verificationCode.trim();
    if (!/^\d{6}$/.test(normalizedCode)) {
      setErrors({ 'sign-up-verification-code': 'Enter the 6-digit code from your email.' });
      return;
    }

    setPending(true);
    setResendStatus(null);
    try {
      const result = await verifyCustomerEmail(email.trim(), normalizedCode);
      clear();
      setCreated(true);
      onSignedUp?.({ ok: true, customerId: result.userId });
    } catch {
      setErrors({ 'sign-up-verification-code': VERIFICATION_FAILURE });
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    setPending(true);
    clear('sign-up-verification-code');
    setResendStatus(null);
    try {
      await resendCustomerVerificationCode(email.trim());
      setVerificationCode('');
      setResendStatus('A new verification code has been sent if this account is eligible.');
    } catch {
      setResendStatus('We could not send another code right now. Please wait and try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      aria-labelledby="sign-up-heading"
      style={{ maxWidth: '38rem', margin: '0 auto', padding: space[6] }}
    >
      <h1
        id="sign-up-heading"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        {awaitingVerification && !created
          ? 'Verify your email'
          : 'Create your Beauty Burst account'}
      </h1>
      <p
        style={{
          ...typeStyle('body'),
          color: semanticColor.textSecondary,
          margin: `${space[2]} 0 ${space[5]}`,
        }}
      >
        {awaitingVerification && !created
          ? `Enter the 6-digit code we sent to ${email.trim()}.`
          : 'Save your details for a more convenient local beauty shopping experience.'}
      </p>
      <Card>
        {created ? (
          <p
            role="status"
            style={{ ...typeStyle('body'), color: semanticColor.textPrimary, margin: 0 }}
          >
            Your email is verified and your account is ready.
          </p>
        ) : awaitingVerification ? (
          <>
            <ErrorSummary errors={errorsForSummary(errors)} />
            <form noValidate onSubmit={verify} style={{ display: 'grid', gap: space[4] }}>
              <FormField
                id="sign-up-verification-code"
                label="Verification code"
                hint="The code expires after 15 minutes."
                error={errors['sign-up-verification-code']}
                required
              >
                <input
                  type="text"
                  name="verificationCode"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(event) => {
                    setVerificationCode(event.currentTarget.value.replace(/\D/g, '').slice(0, 6));
                    clear('sign-up-verification-code');
                  }}
                />
              </FormField>
              <Button type="submit" disabled={pending}>
                {pending ? 'Verifying…' : 'Verify email'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={pending}
                onClick={() => void resend()}
              >
                Send a new code
              </Button>
              {resendStatus !== null ? (
                <p role="status" style={{ ...typeStyle('bodySm'), margin: 0 }}>
                  {resendStatus}
                </p>
              ) : null}
            </form>
          </>
        ) : (
          <>
            <ErrorSummary errors={errorsForSummary(errors)} />
            <form noValidate onSubmit={submit} style={{ display: 'grid', gap: space[4] }}>
              <FormField
                id="sign-up-display-name"
                label="Display name"
                error={errors['sign-up-display-name']}
                required
              >
                <input
                  type="text"
                  name="displayName"
                  autoComplete="name"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.currentTarget.value);
                    clear('sign-up-display-name');
                  }}
                />
              </FormField>
              <FormField
                id="sign-up-email"
                label="Email address"
                error={errors['sign-up-email']}
                required
              >
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.currentTarget.value);
                    clear('sign-up-email');
                  }}
                />
              </FormField>
              <FormField
                id="sign-up-password"
                label="Password"
                hint="Use a password that meets our account security requirements."
                error={errors['sign-up-password']}
                required
              >
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                    clear('sign-up-password');
                  }}
                />
              </FormField>
              <FormField
                id="sign-up-phone"
                label="Phone number (optional)"
                error={errors['sign-up-phone']}
              >
                <input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.currentTarget.value);
                    if (event.currentTarget.value.trim().length === 0)
                      setMarketingSmsConsent(false);
                    clear('sign-up-phone');
                  }}
                />
              </FormField>
              <FormField
                id="sign-up-postal-code"
                label="ZIP code (optional)"
                error={errors['sign-up-postal-code']}
              >
                <input
                  type="text"
                  name="postalCode"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  value={postalCode}
                  onChange={(event) => {
                    setPostalCode(event.currentTarget.value);
                    clear('sign-up-postal-code');
                  }}
                />
              </FormField>
              <fieldset
                style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: space[3] }}
              >
                <legend style={{ ...typeStyle('label'), color: semanticColor.textPrimary }}>
                  Marketing preferences (optional)
                </legend>
                <label
                  htmlFor="sign-up-marketing-email"
                  style={{
                    ...typeStyle('bodySm'),
                    color: semanticColor.textPrimary,
                    display: 'flex',
                    gap: space[2],
                  }}
                >
                  <input
                    id="sign-up-marketing-email"
                    type="checkbox"
                    name="marketingEmailConsent"
                    checked={marketingEmailConsent}
                    onChange={(event) => setMarketingEmailConsent(event.currentTarget.checked)}
                    style={{ width: input.height, height: input.height, margin: 0 }}
                  />
                  Email me Beauty Burst offers and news.
                </label>
                <label
                  htmlFor="sign-up-marketing-sms"
                  style={{
                    ...typeStyle('bodySm'),
                    color: semanticColor.textPrimary,
                    display: 'flex',
                    gap: space[2],
                  }}
                >
                  <input
                    id="sign-up-marketing-sms"
                    type="checkbox"
                    name="marketingSmsConsent"
                    checked={marketingSmsConsent}
                    disabled={phone.trim().length === 0}
                    onChange={(event) => setMarketingSmsConsent(event.currentTarget.checked)}
                    style={{ width: input.height, height: input.height, margin: 0 }}
                  />
                  Send Beauty Burst offers by text message. Message and data rates may apply.
                </label>
                {phone.trim().length === 0 ? (
                  <p
                    style={{
                      ...typeStyle('bodySm'),
                      color: semanticColor.textSecondary,
                      margin: 0,
                    }}
                  >
                    Add a phone number to choose marketing text messages.
                  </p>
                ) : null}
              </fieldset>
              <Button
                type="submit"
                disabled={pending}
                aria-label={pending ? 'Creating account…' : 'Create account'}
              >
                {pending ? 'Creating account…' : 'Create account'}
              </Button>
            </form>
          </>
        )}
        <p
          style={{
            ...typeStyle('bodySm'),
            color: semanticColor.textSecondary,
            margin: `${space[4]} 0 0`,
          }}
        >
          Already have an account?{' '}
          <a href={customerHref('/sign-in')} style={{ color: semanticColor.textLink }}>
            Sign in
          </a>
          .
        </p>
      </Card>
    </section>
  );
}
